import { ButtonProps, useMediaQuery, useTheme } from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import { useFormState } from 'hooks/useFormState';
import React, { useState } from 'react';
import {
    FieldValues,
    useFormContext,
    SubmitHandler,
    SubmitErrorHandler,
} from 'react-hook-form';

export interface FormSubmitButtonProps<TFieldValues extends FieldValues>
    extends ButtonProps {
    onValidHandler: SubmitHandler<TFieldValues>;
    onErrorHandler: SubmitErrorHandler<TFieldValues>;
    disabled?: boolean;
    /** Override default button label, which is "Submit". */
    buttonLabel?: string;
    /** Optional argument to set loading state while using [LoadingButton](https://mui.com/material-ui/api/loading-button/) functionality. */
    isLoading?: boolean;
    /** Optional icon placed at the start of text on the button. */
    startIcon?: JSX.Element;
}

/**
 * submit button for integration with react-hook-form
 * @param onValidHandler - handler for when validation is successful
 * @param onErrorHandler - handler for when validation is unsuccessful
 * @param {boolean} disabled - is this component readonly?
 * @param {string} className - for styling
 * @param {string} buttonLabel - Optional override default button label, which is "Submit".
 * @param {boolean} isLoading - Optional argument to set loading state while using [LoadingButton](https://mui.com/material-ui/api/loading-button/) functionality.
 * @param {JSX.Element} startIcon - Optional icon placed at the start of text on the button.
 * @returns FormSubmitButton
 */
export function FormSubmitButton<TFieldValues extends FieldValues>({
    onValidHandler,
    onErrorHandler,
    disabled,
    className,
    buttonLabel,
    isLoading = false,
    startIcon,
    ...props
}: FormSubmitButtonProps<TFieldValues>) {
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
    const {
        formSections,
        currentFormSectionIndex,
        setCurrentSectionIndex,
        formSectionErrors,
    } = useFormState();

    const { handleSubmit, trigger } = useFormContext();
    const [formSectionHasErrors, setFormSectionHasErrors] = useState(false);

    const formHasMultiplePages = formSections.length > 0;

    const ifOnLastFormSection =
        !formSections.length ||
        formSections.length - 1 === currentFormSectionIndex;

    const handleFormSubmit = handleSubmit(
        onValidHandler as SubmitHandler<FieldValues>,
        onErrorHandler as SubmitErrorHandler<FieldValues>
    );

    const handleOnClick = async (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        if (formHasMultiplePages) {
            const isOnLastPage =
                currentFormSectionIndex === formSections.length - 1;

            const fieldsToValidate = formSectionErrors
                .filter(
                    (e) =>
                        e.section === formSections[currentFormSectionIndex] ||
                        isOnLastPage
                )
                .map((e) => e.field.name);

            const sectionHasNoErrors = await trigger(fieldsToValidate, {
                shouldFocus: true,
            });

            if (sectionHasNoErrors) {
                setFormSectionHasErrors(false);

                if (ifOnLastFormSection) {
                    return handleFormSubmit(e);
                }

                return setCurrentSectionIndex(currentFormSectionIndex + 1);
            } else {
                return setFormSectionHasErrors(true);
            }
        }
        return handleFormSubmit(e);
    };

    return (
        <LoadingButton
            onClick={handleOnClick}
            variant="contained"
            type="submit"
            data-testid="form-submit-button"
            className={className}
            disabled={disabled}
            color={formSectionHasErrors ? 'error' : 'primary'}
            fullWidth={props.fullWidth ?? fullScreen}
            loading={isLoading}
            startIcon={startIcon ? startIcon : null}
            loadingPosition={startIcon ? 'start' : undefined}
            {...props}
        >
            {buttonLabel
                ? buttonLabel
                : ifOnLastFormSection
                ? 'Submit'
                : 'Next'}
        </LoadingButton>
    );
}
